import React, {useMemo, useState} from 'react';
import { useForm, Controller } from 'react-hook-form';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import ExpanderPanel from '@rio-cloud/rio-uikit/lib/es/ExpanderPanel';
import {useCm4SetLogLevelMutation} from '~/api/logs/LoggingService.api';
import Notification from '@rio-cloud/rio-uikit/Notification';
import SelectOTA from '~/features/base/components/forms/ota/SelectOTA';
import InputOTA from "~/features/base/components/forms/ota/InputOTA";
import {CM4LogLevelRequestDto} from "~/api/models/CM4LogLevelRequestDto";
import {createCM4LogLevelFilterOptions} from "~/features/deviceLogs/constants/CM4LogLevelOptions";
import OverlayTrigger from "@rio-cloud/rio-uikit/lib/es/OverlayTrigger";
import Tooltip from "@rio-cloud/rio-uikit/lib/es/Tooltip";

interface CM4SetDeviceLogLevelProps {
    serialNumber: string;
    isDeviceLogsUser: boolean;
    lastEssentialImageReported: any;
}

const CM4SetDeviceLogLevel: React.FC<CM4SetDeviceLogLevelProps> = ({ serialNumber, isDeviceLogsUser, lastEssentialImageReported }) => {

    const {
        handleSubmit,
        formState: { errors },
        control,
        reset,
    } = useForm({
        defaultValues: {
            applicationId: '',
            contextId: '',
            duration: '',
            level: null
        }
    });

    const isDisabled = useMemo(
        () => {
            const version = lastEssentialImageReported?.version;
            if (!version) return false;

            const { major, minor, patch } = version;
            const isVersionHigher = major > 17 || (major === 17 && minor === 1 && patch >= 1);
            const is17_2 = major === 17 && minor === 2;
            return !(isVersionHigher && !is17_2);
        },
        [lastEssentialImageReported]
    );

    const logLevelOptions = createCM4LogLevelFilterOptions();

    const [setLogLevel] = useCm4SetLogLevelMutation();

    const buttonClassName = { disabled: !isDeviceLogsUser};

    const handleRequest = async(requestParams: CM4LogLevelRequestDto) => {

        await setLogLevel(requestParams)
            .unwrap()
            .then(() => {
                Notification.success(<FormattedMessage
                    id='intl-msg:logs.logLevelSuccessMsg'/>);
                reset({
                    applicationId: '',
                    contextId: '',
                    duration: '',
                    level: null
                });
            })
            .catch((error) => {
                console.error(error);
                Notification.error(<FormattedMessage id='intl-msg:logs.logLevelErrorMsg'/>);
            });

    };

    const onSubmit = data => {
        const requestParams: CM4LogLevelRequestDto = {
            serialNumber: serialNumber,
            level: data.level.id,
            applicationId: data.applicationId,
            contextId: data.contextId,
            duration: data.duration
        };
        handleRequest(requestParams);
    };

    const logLevelPlaceholder = <FormattedMessage id='intl-msg:logs.logLevelPlaceholder'/>;

    const content = (
        <div>
        <ExpanderPanel
            disabled={isDisabled}
            title={
            <div>
                <span className={'h5'}>
                    <FormattedMessage id='intl-msg:editDeviceServiceLogLevel'/>
                </span>
            </div>}
                       bsStyle='blank'>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className={'display-flex gap-10 justify-content-around'}>
                    <div className={'width-50pct'}>
                        <Controller
                            control={control}
                            name='applicationId'
                            rules={{
                                required: {
                                    value: true,
                                    message: 'intl-msg:form:requiredField',
                                },
                                minLength: {
                                    value: 4,
                                    message: 'intl-msg:form.4characters',
                                },
                                maxLength: {
                                    value: 4,
                                    message: 'intl-msg:form.4characters',
                                },
                            }}
                            render={({ field : {onChange, value}}) =>
                                <>
                                    <InputOTA
                                        title={'intl-msg:applicationId'}
                                        type='text'
                                        className='form-control'
                                        value={value || ''}
                                        placeholder={'Enter application ID ..'}
                                        onChange={onChange}
                                        hasError={!!errors.applicationId}
                                        errorMsg={errors?.applicationId?.message}
                                    />
                                </>
                            }
                        />
                    </div>
                    <div className={'width-50pct'}>
                        <Controller
                            control={control}
                            name='contextId'
                            rules={{
                                required: {
                                    value: true,
                                    message: 'intl-msg:form:requiredField',
                                },
                                maxLength: {
                                    value: 4,
                                    message: 'intl-msg:form.4characters.max',
                                },
                            }}
                            render={({ field : {onChange, value}}) =>
                                <>
                                    <InputOTA
                                        title={'intl-msg:contextId'}
                                        type='text'
                                        className='form-control'
                                        value={value || ''}
                                        placeholder={'Enter context ID ..'}
                                        onChange={onChange}
                                        hasError={!!errors.contextId}
                                        errorMsg={errors?.contextId?.message}
                                    />
                                </>
                            }
                        />
                    </div>
                </div>
                <div className={'display-flex gap-10 justify-content-around margin-top-20 '}>
                    <div className={'width-50pct'}>
                        <Controller
                            control={control}
                            name='duration'
                            rules={{ required: true }}
                            render={({ field : {onChange, value}}) =>
                                <>
                                    <InputOTA
                                        title={'intl-msg:duration'}
                                        type='number'
                                        className='form-control'
                                        value={value || ''}
                                        placeholder={'Enter the duration in minutes ..'}
                                        onChange={onChange}
                                        hasError={!!errors.duration}
                                    />
                                </>
                            }
                        />
                    </div>

                    <div className={'width-50pct'}>
                        <label className='control-label'>
                            <FormattedMessage id='intl-msg:logLevel'/>
                        </label>
                        <Controller
                            control={control}
                            name='level'
                            rules={{ required: true }}
                            render={({ field : {onChange, value}}) =>
                                <>
                                    <SelectOTA
                                        hasError={!!errors.level}
                                        placeholder={logLevelPlaceholder}
                                        options={logLevelOptions}
                                        value={value?.id || null}
                                        onChange={onChange}
                                    />
                                </>
                            }
                        />
                    </div>
                </div>
                <div className={'margin-top-20 display-flex justify-content-end align-items-end'}>
                    <button type={'submit'} className={classNames('btn btn-primary', buttonClassName)} onClick={()=>{}}>
                        <FormattedMessage id='intl-msg:editDeviceServiceLogLevel'/>
                    </button>
                </div>
            </form>
        </ExpanderPanel>
        </div>
    );

    return isDisabled ? (
        <OverlayTrigger
            key={1}
            placement={'top-start'}
            overlay={
                <Tooltip id='tooltip' >
                    <FormattedMessage id={'intl-msg:logLevel.cm4.disabledTooltip'}/>
                </Tooltip>
            }>
            {content}
        </OverlayTrigger>
    ) : content;
};

export default CM4SetDeviceLogLevel;
